import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AnimationIcon from '@mui/icons-material/Animation';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import HistoryIcon from '@mui/icons-material/History';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import { TABLE_THEME_ORDER, TABLE_THEMES, TableThemeId } from 'constants/tableThemes';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import GlobalContext from 'contexts/GlobalContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useFeatureFlagExposure } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { useHandHistory } from 'hooks/useHandHistory';
import { useLeaveTable } from 'hooks/useLeaveTable';
import { useOrientation } from 'hooks/useOrientation';
import { usePanelSlide } from 'hooks/usePanelSlide';
import { useTableScopedOpen } from 'hooks/useTableScopedOpen';
import { useTableId } from 'hooks/useTableSelector';
import { currencyFormatter } from 'utils/functions';
import { safeAreaPanelInsets } from 'utils/safeArea';
import { APP_DOMAIN } from 'utils/urls';

import { CashierContext } from 'components/Cashier/CashierContext';
import TableViewSizeControl from 'components/TableSettings/TableViewSizeControl';
import TableViewSizePreview from 'components/TableSettings/TableViewSizePreview';
import VersionLabel from 'components/VersionLabel';
import AvatarFrame from 'components/Streaks/AvatarFrame';
import { useOwnStreakFrame } from 'components/Streaks/state/useSeatStreakFrame';

import { useStackTablesToggle } from './useStackTablesToggle';

type SubView = 'settings' | 'tableTheme' | null;
// Off-screen position the panel slides in from (left-hand edge, under the hamburger).
const PANEL_HIDDEN = { x: '-100%' };

// Hover/press helper for inline-styled controls (no :hover/:active with
// CSSProperties). Applies `hover` styles on pointer enter and touch start.
const hoverStyle = (hover: Partial<CSSStyleDeclaration>, rest: Partial<CSSStyleDeclaration>) => ({
  onMouseEnter: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, hover),
  onMouseLeave: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest),
  onTouchStart: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, hover),
  onTouchEnd: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest),
  onTouchCancel: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest)
});

const iconBtnHover = hoverStyle(
  { background: 'rgba(255,255,255,0.12)', color: '#fff' },
  { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }
);

const rowHover = hoverStyle(
  { background: 'rgba(255,255,255,0.06)' },
  { background: 'transparent' }
);

const settingsRowHover = hoverStyle(
  { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(232,130,31,0.4)' },
  { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(232,130,31,0.18)' }
);

/**
 * In-table hamburger menu (EN parity redesign). A left slide-in panel with a
 * streamlined, table-focused menu — Go back to Lobby, Cashier, Table Settings,
 * Table History, Exit Table — plus the player's balance header. Distinct from
 * the lobby AccountMenuPanel so the lobby menu is unaffected.
 */
const TableMenuPanel = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const playerAvatar = useAppSelector((state) => state.user.playerAvatar);
  const displayName = useAppSelector(
    (state) => state.user.displayName || state.user.playerName || 'Player'
  );
  const cashBalance = useAppSelector((state) => state.user.playerBankAmount);
  const promoBalance = useAppSelector((state) => state.user.playerChargeBalance);
  const streakFrame = useOwnStreakFrame();
  // Cashier is unavailable during maintenance (Unity parity) — the row is
  // disabled here (matching AccountMenuPanel), and openCashier guards itself.
  // Every other row (Lobby / Settings / History / Exit) stays available.
  const isMaintenance = useAppSelector(
    (state) => (state as any).maintenanceSchedule?.isMaintenance
  );

  const { openCashier, setOpenedFromMenu } = useContext(CashierContext);
  const { returnToLobby } = useContext(ViewManagerContext);
  const { isMuted, setIsMuted } = useContext(GlobalContext);
  const { setIsOpen: setHandHistoryOpen } = useHandHistory();
  const tableSettings = useContext(TableSettingsContext);
  const animations = useAnimationPreferences();
  // Shared with AccountMenuPanel — one definition for both menus.
  const stackTablesToggle = useStackTablesToggle();
  const { leave, isHidden: exitHidden, exitLabel } = useLeaveTable();

  // The table this in-table menu belongs to; used to scope a table-specific
  // theme (and to resolve which theme this table currently shows).
  const gameID = useTableId();
  const currentTableTheme = tableSettings.getThemeForGame(gameID);

  const [subView, setSubView] = useState<SubView>(null);
  const [draftTheme, setDraftTheme] = useState<TableThemeId>(currentTableTheme);
  // "Set this theme for All My Tables" — off by default: a selection applies to
  // THIS table only. When on, Save sets the global theme (and clears every
  // per-table override) instead.
  const [applyToAll, setApplyToAll] = useState(false);
  // This panel portals to <body>, so a parked table's open menu kept painting
  // over the table the player swiped to. Treat "not the table I'm on" as closed
  // — it slides out, and slides back in on return (see useTableScopedOpen).
  const isOpen = useTableScopedOpen(open);
  useFeatureFlagExposure('reduceAnimations', 'settings', isOpen && subView === 'settings');
  // Defer unmount until the close slide-out finishes.
  const [rendered, setRendered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setRendered(true);
  }, [isOpen]);

  // Reset to the root menu whenever the panel is (re)opened.
  useEffect(() => {
    if (isOpen) setSubView(null);
  }, [isOpen]);

  // Slide from the LEFT (the hamburger sits top-left). Defers unmount so the
  // close tween plays out instead of popping away.
  usePanelSlide({
    open: isOpen,
    rendered,
    panelRef,
    backdropRef,
    hidden: PANEL_HIDDEN,
    onClosed: () => setRendered(false)
  });

  if (!rendered) return null;

  const handleGoToLobby = () => {
    onClose();
    returnToLobby();
  };

  const handleOpenCashier = () => {
    // Opened from the in-table menu, NOT the lobby account menu: the cashier's
    // back arrow should just close it (returning to the table), not reopen the
    // account menu / profile. `openedFromMenu` drives that "return to menu"
    // behavior, so keep it false here.
    setOpenedFromMenu(false);
    onClose();
    openCashier();
  };

  const handleOpenTableHistory = () => {
    onClose();
    setHandHistoryOpen(true);
  };

  const handleExitTable = () => {
    onClose();
    leave();
  };

  const menuView = (
    <>
      <div style={headerStyle}>
        <button onClick={onClose} style={backBtn} aria-label='Close menu' {...iconBtnHover}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <AvatarFrame tier={streakFrame} size={AVATAR_SIZE}>
          <div style={streakFrame ? { ...avatarWrap, border: 'none' } : avatarWrap}>
            {playerAvatar ? (
              <img
                src={`https://media.${APP_DOMAIN}/images/${playerAvatar}`}
                alt=''
                style={avatarImg}
              />
            ) : (
              <AccountCircleIcon style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.4)' }} />
            )}
          </div>
        </AvatarFrame>
        <div style={headerInfo}>
          <span style={displayNameStyle}>{displayName}</span>
          {/* "BALANCE" section header — mirrors AccountMenuPanel so the
              Cash / Promo rows have a labelled group in both panels. Was
              missing here, which made the table-menu header look like the
              amounts were floating attributes of the display name. */}
          <span style={balanceLabelStyle}>BALANCE</span>
          <span style={balanceLineStyle}>
            <span style={balanceMutedStyle}>Cash </span>
            <span style={cashAmountStyle}>{currencyFormatter(cashBalance || 0)}</span>
          </span>
          <span style={balanceLineStyle}>
            <span style={balanceMutedStyle}>Promo </span>
            <span style={promoAmountStyle}>{currencyFormatter(promoBalance || 0)}</span>
          </span>
        </div>
      </div>

      <div className='table-menu-scroll' style={contentStyle}>
        <div style={menuListStyle}>
          <MenuRow
            icon={<HomeOutlinedIcon style={menuIconStyle} />}
            label='Go back to Lobby'
            onClick={handleGoToLobby}
          />
          <MenuRow
            icon={<AccountBalanceIcon style={menuIconStyle} />}
            label='Cashier'
            onClick={handleOpenCashier}
            disabled={isMaintenance}
          />
          <MenuRow
            icon={<SettingsIcon style={menuIconStyle} />}
            label='Table Settings'
            onClick={() => setSubView('settings')}
            chevron
          />
          <MenuRow
            icon={<HistoryIcon style={menuIconStyle} />}
            label='Table History'
            onClick={handleOpenTableHistory}
          />
        </div>
      </div>

      {/* The bar itself is unconditional now that it also carries the version
          readout — the Exit button is what's conditional. Keeping the version
          inside it (rather than after it) is what keeps the safe-area inset on
          the panel's real last element instead of leaving a gap above it. */}
      <div style={exitBarStyle}>
        {!exitHidden && (
          <button onClick={handleExitTable} style={exitBtnStyle}>
            {exitLabel}
          </button>
        )}
        <VersionLabel style={versionFooterStyle} />
      </div>
    </>
  );

  const settingsView = (
    <>
      <div style={subHeaderStyle}>
        <button
          onClick={() => setSubView(null)}
          style={backBtn}
          aria-label='Back'
          {...iconBtnHover}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Table Settings</span>
        <span style={{ width: 28 }} aria-hidden />
      </div>
      <div className='table-menu-scroll' style={contentStyle}>
        <div style={{ ...settingsRowStyle, alignItems: 'flex-start', cursor: 'default' }}>
          <span style={settingsRowIconWrap}>
            <FormatSizeIcon style={settingsRowIconStyle} />
          </span>
          <div style={settingsRowText}>
            <span style={settingsRowLabel}>Table View Size</span>
            <span style={settingsRowDesc}>
              Cards, seat names and money/pot/chip text — Small to Largest.
            </span>
            {/* Shared with AccountMenuPanel — the player can reach this control
                from either menu, so it must behave identically in both. */}
            <TableViewSizeControl testIdPrefix='table-view-size' />
            <TableViewSizePreview scale={tableSettings.tableViewScale} />
          </div>
        </div>
        <ToggleRow
          icon={<ViewAgendaOutlinedIcon style={settingsRowIconStyle} />}
          {...stackTablesToggle}
        />
        <button
          type='button'
          onClick={() => {
            setDraftTheme(currentTableTheme);
            setApplyToAll(false);
            setSubView('tableTheme');
          }}
          style={settingsRowStyle}
          {...settingsRowHover}>
          <span style={settingsRowIconWrap}>
            <PaletteIcon style={settingsRowIconStyle} />
          </span>
          <div style={settingsRowText}>
            <span style={settingsRowLabel}>Change table theme</span>
            <span style={settingsRowDesc}>{TABLE_THEMES[currentTableTheme].name}</span>
          </div>
          <ChevronRightIcon style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.4)' }} />
        </button>
        <ToggleRow
          icon={<ChatBubbleOutlineIcon style={settingsRowIconStyle} />}
          label='Table chat enabled'
          description='Show chat messages at the poker table'
          checked={tableSettings.chatEnabled}
          onChange={tableSettings.setChatEnabled}
        />
        <ToggleRow
          icon={<NotificationsOffOutlinedIcon style={settingsRowIconStyle} />}
          label='Table sounds disabled'
          description='Silence card deals, chips, and timer alerts'
          checked={isMuted}
          onChange={setIsMuted}
        />
        {/* Hidden while ALL table sounds are off — it would have no effect, and
            Unity hides it the same way (MenuForTablesView.cs:120/242/493). The
            saved value is untouched, so re-enabling sounds brings the row back
            exactly as the player left it. */}
        {!isMuted && (
          <ToggleRow
            icon={<PersonOffOutlinedIcon style={settingsRowIconStyle} />}
            label="Mute Other Players' Sounds"
            description='Silence sound effects from other players at the table.'
            checked={tableSettings.muteOtherPlayers}
            onChange={tableSettings.setMuteOtherPlayers}
          />
        )}
        {/* EN-11003: account-wide setting behind release.reduce-animations. */}
        {animations.available && (
          <>
            <ToggleRow
              icon={<AnimationIcon style={settingsRowIconStyle} />}
              label='Reduce animations'
              description='Skip confetti and shorten win celebrations'
              checked={animations.ready && animations.reduceAnimations}
              disabled={!animations.ready || animations.busy}
              onChange={animations.setReduceAnimations}
            />
            {animations.busy && (
              <span role='status' style={settingsRowDesc}>
                Updating animation settings…
              </span>
            )}
            {animations.error && (
              <div role='alert' style={settingsRowDesc}>
                {animations.error}{' '}
                <button type='button' onClick={animations.retry}>
                  Try again
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const tableThemeView = (
    <>
      <div style={subHeaderStyle}>
        <button
          onClick={() => setSubView('settings')}
          style={backBtn}
          aria-label='Back'
          {...iconBtnHover}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Table Theme</span>
        <span style={{ width: 28 }} aria-hidden />
      </div>
      <div className='table-menu-scroll' style={{ ...contentStyle, paddingBottom: 90 }}>
        <div style={themeGridStyle}>
          {TABLE_THEME_ORDER.map((id) => {
            const t = TABLE_THEMES[id];
            const isSelected = draftTheme === id;
            return (
              <button
                key={id}
                onClick={() => setDraftTheme(id)}
                style={{
                  ...themeCardStyle,
                  borderColor: isSelected ? '#E98C26' : 'rgba(255,255,255,0.08)',
                  boxShadow: isSelected ? '0 0 0 1px #E98C26' : 'none'
                }}>
                <div style={{ ...themePreviewStyle, backgroundColor: t.swatch.outer }}>
                  <div
                    style={{
                      ...themePreviewOvalStyle,
                      backgroundColor: t.swatch.felt,
                      boxShadow: `0 0 0 1px ${t.swatch.ring}, inset 0 0 12px rgba(0,0,0,0.4)`
                    }}
                  />
                </div>
                <span style={themeNameStyle}>{t.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={exitBarStyle}>
        <label style={applyAllRowStyle}>
          <input
            type='checkbox'
            checked={applyToAll}
            onChange={(e) => setApplyToAll(e.target.checked)}
            style={applyAllCheckboxStyle}
            aria-label='Set this theme for all my tables'
          />
          <span>Set this theme for All My Tables</span>
        </label>
        <button
          onClick={() => {
            // Checked → global ("All My Tables"): sets the default and clears
            // every per-table override. Unchecked → this table only.
            if (applyToAll) {
              tableSettings.setTheme(draftTheme);
            } else {
              tableSettings.setTableTheme(gameID, draftTheme);
            }
            setSubView('settings');
          }}
          style={themeSaveBtn}
          // A no-op only when applying to THIS table and the theme is unchanged;
          // applying to all is always actionable (it may reset other tables).
          disabled={!applyToAll && draftTheme === currentTableTheme}>
          Save changes
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      <style>{tableMenuScrollbarCss}</style>
      {/* No ForcePortrait here (unlike the lobby AccountMenuPanel): the in-table
          menu must follow the table's real orientation — a full-screen sheet in
          portrait, a left drawer in landscape — not rotate to portrait. */}
      <div ref={backdropRef} style={backdropStyle} onClick={onClose} />
      <div ref={panelRef} style={isPortrait ? panelMobile : panelDesktop}>
        {subView === null && menuView}
        {subView === 'settings' && settingsView}
        {subView === 'tableTheme' && tableThemeView}
      </div>
    </>,
    document.body
  );
};

export default TableMenuPanel;

const MenuRow = ({
  icon,
  label,
  onClick,
  chevron,
  disabled
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  chevron?: boolean;
  disabled?: boolean;
}) => (
  <button
    type='button'
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    aria-disabled={disabled}
    style={{ ...menuRowStyle, ...(disabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
    {...(disabled ? {} : rowHover)}>
    <span style={menuRowIconWrap}>{icon}</span>
    <span style={menuRowLabelStyle}>{label}</span>
    {chevron ? (
      <ChevronRightIcon style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.3)' }} />
    ) : (
      <span aria-hidden style={{ width: 14 }} />
    )}
  </button>
);

const ToggleRow = ({
  icon,
  label,
  description,
  checked,
  disabled,
  onChange
}: {
  icon?: React.ReactNode;
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) => (
  <button
    type='button'
    onClick={() => !disabled && onChange(!checked)}
    style={{ ...settingsRowStyle, opacity: disabled ? 0.6 : 1 }}
    aria-pressed={checked}
    disabled={disabled}
    {...settingsRowHover}>
    {icon && <span style={settingsRowIconWrap}>{icon}</span>}
    <div style={settingsRowText}>
      <span style={settingsRowLabel}>{label}</span>
      {description && <span style={settingsRowDesc}>{description}</span>}
    </div>
    <span
      style={{
        ...toggleTrackStyle,
        background: checked ? 'rgba(232,130,31,0.7)' : 'rgba(255,255,255,0.12)',
        borderColor: checked ? 'rgba(232,130,31,0.9)' : 'rgba(255,255,255,0.2)'
      }}>
      <span
        style={{ ...toggleKnobStyle, transform: checked ? 'translateX(18px)' : 'translateX(0)' }}
      />
    </span>
  </button>
);

/* ─────────── styles ─────────── */

const tableMenuScrollbarCss = `
  .table-menu-scroll { scrollbar-width: thin; scrollbar-color: rgba(233,140,38,0.3) transparent; }
  .table-menu-scroll::-webkit-scrollbar { width: 6px; }
  .table-menu-scroll::-webkit-scrollbar-track { background: transparent; }
  .table-menu-scroll::-webkit-scrollbar-thumb { background: rgba(233,140,38,0.3); border-radius: 999px; }
`;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  zIndex: 900,
  opacity: 0
};

const panelBase: CSSProperties = {
  position: 'fixed',
  backgroundColor: 'rgba(12,18,28,0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 950,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  ...safeAreaPanelInsets
};

const panelMobile: CSSProperties = {
  ...panelBase,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  height: '100vh',
  boxShadow: '0 -8px 40px rgba(0,0,0,0.5)'
};

const panelDesktop: CSSProperties = {
  ...panelBase,
  top: 0,
  left: 0,
  bottom: 0,
  width: '380px',
  borderTopRightRadius: '16px',
  borderBottomRightRadius: '16px',
  boxShadow: '8px 0 40px rgba(0,0,0,0.5)',
  borderRight: '1px solid rgba(255,255,255,0.06)'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '20px 20px 16px',
  flexShrink: 0,
  borderBottom: '1px dashed rgba(232,130,31,0.18)'
};

const backBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: 'none',
  borderRadius: '999px',
  width: 28,
  height: 28,
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background-color 0.15s ease, color 0.15s ease'
};

// Outer size, border included, so the streak frame fits it exactly.
const AVATAR_SIZE = 52;

const avatarWrap: CSSProperties = {
  position: 'relative',
  boxSizing: 'border-box',
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '2px solid rgba(233,140,38,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0
};

const avatarImg: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%'
};

const headerInfo: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0
};

const displayNameStyle: CSSProperties = {
  fontFamily: "'Rye', serif",
  fontSize: '18px',
  color: '#ffb45c',
  letterSpacing: '0.02em',
  lineHeight: 1,
  textShadow: '0 1px 0 rgba(0, 0, 0, 0.55)',
  wordBreak: 'break-word',
  marginBottom: 2
};

// Kept in step with AccountMenuPanel's `balanceLabelStyle` so the two
// panels' BALANCE headers look identical — same tracked-caps Special Elite
// styling, same muted bone tone.
const balanceLabelStyle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '9.5px',
  color: '#d9c79e',
  opacity: 0.7,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  marginTop: 2
};

const balanceLineStyle: CSSProperties = { fontSize: '0.78rem', fontWeight: 600 };

const balanceMutedStyle: CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  fontSize: '12px',
  color: '#d9c79e'
};

const cashAmountStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '13.5px',
  color: '#f1e3c7',
  letterSpacing: '0.01em'
};

const promoAmountStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '13.5px',
  color: '#ffb45c',
  letterSpacing: '0.01em',
  textShadow: '0 0 6px rgba(232, 130, 31, 0.25)'
};

const versionFooterStyle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.7rem',
  letterSpacing: '0.02em',
  color: 'rgba(241, 227, 199, 0.45)',
  justifyContent: 'center',
  marginTop: 10
};

const contentStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  overflowY: 'auto'
};

const menuListStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '2px' };

const menuRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '38px 1fr 14px',
  columnGap: '12px',
  alignItems: 'center',
  width: '100%',
  padding: '15px 6px',
  background: 'transparent',
  border: 0,
  borderBottom: '1px dotted rgba(255, 255, 255, 0.06)',
  color: '#f1e3c7',
  font: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  borderRadius: 8,
  transition: 'background 0.15s ease'
};

const menuRowIconWrap: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(232, 130, 31, 0.32)',
  color: '#f1e3c7',
  display: 'grid',
  placeItems: 'center',
  justifySelf: 'center',
  flexShrink: 0
};

const menuIconStyle: CSSProperties = { fontSize: '1.05rem', color: '#f1e3c7' };

const menuRowLabelStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#f1e3c7'
};

const subHeaderStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '28px 1fr 28px',
  alignItems: 'center',
  padding: '20px 20px 14px',
  backgroundImage:
    'repeating-linear-gradient(to right, rgba(232,130,31,0.55) 0 9px, transparent 9px 15px)',
  backgroundSize: '100% 2px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom left',
  flexShrink: 0,
  textAlign: 'center'
};

const subHeaderTitle: CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '18px',
  color: '#f1e3c7',
  letterSpacing: '0.04em'
};

const settingsRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
  padding: '14px 12px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(232,130,31,0.18)',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  transition: 'background-color 0.15s ease, border-color 0.15s ease'
};

const settingsRowIconWrap: CSSProperties = {
  width: 28,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0
};

const settingsRowIconStyle: CSSProperties = {
  fontSize: '1.3rem',
  color: 'rgba(241, 227, 199, 0.9)'
};

const settingsRowText: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minWidth: 0
};

const settingsRowLabel: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 700,
  letterSpacing: '0.01em',
  color: '#f1e3c7'
};

const settingsRowDesc: CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.5)',
  lineHeight: 1.35
};

const toggleTrackStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: 38,
  height: 22,
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.12)',
  flexShrink: 0,
  transition: 'background-color 0.18s ease, border-color 0.18s ease'
};

const toggleKnobStyle: CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
  transition: 'transform 0.18s ease'
};

const themeGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '14px'
};

const themeCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '14px 10px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  border: '2px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
};

const themePreviewStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '0.85 / 1',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const themePreviewOvalStyle: CSSProperties = {
  width: '70%',
  height: '70%',
  borderRadius: '999px'
};

const themeNameStyle: CSSProperties = { fontSize: '0.8rem', fontWeight: 600, color: '#fff' };

const exitBarStyle: CSSProperties = {
  padding: '14px 20px',
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  backgroundColor: 'rgba(12,18,28,0.98)',
  flexShrink: 0
};

const exitBtnStyle: CSSProperties = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(180deg, #c9332a, #6b1a14)',
  border: '1px solid #2a0805',
  borderRadius: '10px',
  color: '#f1e3c7',
  fontSize: '1rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: "'Oswald', sans-serif",
  letterSpacing: '0.02em'
};

const themeSaveBtn: CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'linear-gradient(135deg, #E98C26, #d47a1a)',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};

// "Set this theme for All My Tables" checkbox row above the Save button.
const applyAllRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 12,
  color: 'rgba(255,255,255,0.85)',
  fontSize: '0.85rem',
  fontFamily: 'inherit',
  cursor: 'pointer'
};

const applyAllCheckboxStyle: CSSProperties = {
  width: 18,
  height: 18,
  accentColor: '#E98C26',
  cursor: 'pointer',
  flexShrink: 0
};
